import { useEffect, useState } from "react";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg } from "./kpiFormat";
import { ExpandToggle } from "./ServiceBreakdown";

const NONE = "__none";
const IGNORE = "__ignore";
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

const TypeRow = ({ t, services, canEdit, onSaved }) => {
  const save = async (v) => {
    try {
      const body = { service_id: v === NONE || v === IGNORE ? null : v, ignored: v === IGNORE };
      onSaved((await api.put(`/kpi/appointment-types/${encodeURIComponent(t.id)}`, body)).data);
      toast.success(v === IGNORE ? `${t.name} ignored — its visits are left out of the report` : `${t.name} mapped`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const suggested = services.find((s) => s.id === t.suggested_service)?.name;
  return (
    <tr className="border-t border-zinc-100" data-testid={`kpi-type-row-${slug(t.id)}`}>
      <td className="py-2 pr-3 text-zinc-900">{t.name}</td>
      <td className="py-2 pr-3 text-xs text-zinc-500">{t.ignored ? "—" : suggested || "—"}</td>
      <td className="py-2">
        <Select value={t.ignored ? IGNORE : t.service_id || NONE} onValueChange={save} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-52" data-testid={`kpi-type-service-${slug(t.id)}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not confirmed (use suggestion)</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            <SelectItem value={IGNORE} data-testid={`kpi-type-ignore-${slug(t.id)}`}>Ignore (event)</SelectItem>
          </SelectContent>
        </Select>
      </td>
    </tr>
  );
};

const DeletedTypes = ({ rows }) => {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <div className="text-xs text-zinc-500" data-testid="kpi-types-deleted">
      <div className="flex items-center">
        <ExpandToggle open={open} onToggle={() => setOpen(!open)} testId="kpi-types-deleted-toggle" />
        {rows.length} appointment type(s) deleted in Acuity — left out of the report
      </div>
      {open && <ul className="ml-6 mt-1 space-y-0.5" data-testid="kpi-types-deleted-list">
        {rows.map((t) => <li key={t.id} data-testid={`kpi-type-deleted-${slug(t.id)}`}>{t.name}</li>)}</ul>}
    </div>
  );
};

export const ServiceMapping = ({ canEdit, services }) => {
  const [types, setTypes] = useState(null);
  const load = () => api.get("/kpi/appointment-types").then((r) => setTypes(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const confirmAll = async () => {
    try {
      toast.success(`${(await api.post("/kpi/appointment-types/confirm-suggestions")).data.confirmed} type(s) confirmed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const live = types?.filter((t) => !t.deleted) || [];
  const pending = live.filter((t) => !t.service_id && !t.ignored && t.suggested_service).length;
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-5" data-testid="kpi-service-mapping">
      <div>
        <h3 className="font-display text-lg font-semibold">Services</h3>
        <p className="text-sm text-zinc-500" data-testid="kpi-services-hint">Attendance and visits per member count every service (visits to unmapped
          appointment types show as &ldquo;Not mapped to a service&rdquo;). Map each appointment type below; members are split by the service their
          membership or pack is mapped to in Product mapping. Choose &ldquo;Ignore (event)&rdquo; for events — they, and types deleted in Acuity,
          are left out of the report.</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <h4 className="text-sm font-semibold text-zinc-800">Appointment types → service</h4>
        {canEdit && pending > 0 && <Button size="sm" variant="outline" onClick={confirmAll} data-testid="kpi-types-confirm-all-btn">
          <CheckCheck className="h-4 w-4 mr-1.5" />Confirm {pending} suggestion(s)</Button>}
      </div>
      {!types ? <div className="h-20 animate-pulse rounded bg-zinc-100" /> : types.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="kpi-types-empty">No appointment types yet — they appear after a sync or attendance import.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="pb-2">Appointment type</th><th className="pb-2">Suggested</th><th className="pb-2">Confirmed service</th></tr></thead>
            <tbody>{live.map((t) => <TypeRow key={t.id} t={t} services={services} canEdit={canEdit}
              onSaved={(nt) => setTypes((ts) => ts.map((x) => x.id === nt.id ? { ...x, ...nt } : x))} />)}</tbody>
          </table>
          <DeletedTypes rows={types.filter((t) => t.deleted)} />
        </>
      )}
    </section>
  );
};
